import React, { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Chip,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  PlayArrow as PlayIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  AutoFixHigh as AutoFixHighIcon,
} from '@mui/icons-material';

interface Transcription {
  id: number;
  audioFilePath: string | null;
  transcriptionText: string;
  transcricaoIa: string | null;
  notes: string | null;
  patientName: string | null;
  type: string;
  status: string;
  createdAt: string;
}

interface TranscriptionCardProps {
  transcription: Transcription;
  onDelete: (id: number) => void;
  onEdit: (id: number, field: string, value: string) => void;
  onFormat: (id: number) => void;
  onPlay?: (id: number) => void;
  onCopy?: (id: number) => void;
}

const truncateText = (text: string | undefined | null, maxLength: number): string => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

const TranscriptionCard: React.FC<TranscriptionCardProps> = ({
  transcription,
  onDelete,
  onEdit,
  onFormat,
  onPlay,
  onCopy,
}) => {
  console.log('TranscriptionCard - transcription:', transcription);

  const [showFullText, setShowFullText] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<'transcriptionText' | 'transcricaoIa' | 'notes' | null>(null);
  const [editingText, setEditingText] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEditClick = (field: 'transcriptionText' | 'transcricaoIa' | 'notes', text: string | null) => {
    console.log('handleEditClick - field:', field, 'text:', text);
    setEditingField(field);
    setEditingText(text || '');
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleSaveEdit = () => {
    console.log('handleSaveEdit - field:', editingField, 'text:', editingText);
    if (editingField) {
      onEdit(transcription.id, editingField, editingText);
    }
    setEditDialogOpen(false);
    setEditingField(null);
    setEditingText('');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.error('Data inválida:', dateString);
        return '';
      }
      return format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '';
    }
  };

  const getFieldLabel = (field: string): string => {
    switch (field) {
      case 'transcriptionText':
        return 'Texto Original';
      case 'transcricaoIa':
        return 'Transcrição IA';
      case 'notes':
        return 'Observações';
      default:
        return '';
    }
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" component="h2">
                {transcription.patientName || 'Paciente não informado'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(transcription.createdAt)}
              </Typography>
            </Box>
            <Box display="inline-flex" gap={1}>
              <Chip size="small" label={transcription.type} color="primary" />
              <Chip 
                size="small" 
                label={transcription.status} 
                color={transcription.status === 'pending' ? 'warning' : 'success'} 
              />
            </Box>
          </Box>

          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" color="primary">
                Texto Original
              </Typography>
              <IconButton 
                size="small"
                onClick={() => handleEditClick('transcriptionText', transcription.transcriptionText)}
                title="Editar texto original"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body1" paragraph>
              {showFullText ? transcription.transcriptionText : truncateText(transcription.transcriptionText, 200)}
              {transcription.transcriptionText && transcription.transcriptionText.length > 200 && (
                <Button size="small" onClick={() => setShowFullText(!showFullText)}>
                  {showFullText ? 'Ver menos' : 'Ver mais'}
                </Button>
              )}
            </Typography>
          </Box>

          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" color="primary">
                Transcrição IA
              </Typography>
              <IconButton 
                size="small"
                onClick={() => handleEditClick('transcricaoIa', transcription.transcricaoIa)}
                title="Editar transcrição IA"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body1" paragraph>
              {showFullText ? (transcription.transcricaoIa || 'Não disponível') : truncateText(transcription.transcricaoIa || '', 200)}
              {transcription.transcricaoIa && transcription.transcricaoIa.length > 200 && (
                <Button size="small" onClick={() => setShowFullText(!showFullText)}>
                  {showFullText ? 'Ver menos' : 'Ver mais'}
                </Button>
              )}
            </Typography>
          </Box>

          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" color="primary">
                Observações
              </Typography>
              <IconButton 
                size="small"
                onClick={() => handleEditClick('notes', transcription.notes)}
                title="Editar observações"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body1">
              {transcription.notes || 'Sem observações'}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end' }}>
        {transcription.audioFilePath && (
          <IconButton 
            onClick={() => onPlay?.(transcription.id)}
            title="Reproduzir áudio"
          >
            <PlayIcon />
          </IconButton>
        )}
        
        <IconButton 
          onClick={() => onFormat(transcription.id)}
          title="Formatar texto"
        >
          <AutoFixHighIcon />
        </IconButton>

        <IconButton 
          onClick={handleMenuClick}
          title="Mais opções"
        >
          <MoreVertIcon />
        </IconButton>

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            onCopy?.(transcription.id);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <CopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copiar texto original</ListItemText>
          </MenuItem>
          {transcription.transcricaoIa && (
            <MenuItem onClick={() => {
              onCopy?.(transcription.id);
              handleMenuClose();
            }}>
              <ListItemIcon>
                <CopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Copiar transcrição IA</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={() => {
            if (window.confirm('Tem certeza que deseja excluir esta transcrição?')) {
              onDelete(transcription.id);
            }
            handleMenuClose();
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Excluir</ListItemText>
          </MenuItem>
        </Menu>
      </CardActions>

      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Editar {editingField ? getFieldLabel(editingField) : ''}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={editingField ? getFieldLabel(editingField) : ''}
            fullWidth
            multiline
            rows={8}
            value={editingText}
            onChange={(e) => setEditingText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleSaveEdit} color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default TranscriptionCard;
